import { Icon } from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'
import { SettingsPageQuery } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import type { ModuleAvailabilityType } from 'modules/core/hooks/useModuleAvailability'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'
import type { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'

import { checkAtsEnabled } from 'modules/ats/lib/atsEnabled'
import { useAttendanceContextlessPolicies } from 'modules/attendance/hooks/useAttendanceContextlessPolicies'
import { timeTrackingModuleEnabledInAvailabilities } from 'modules/attendance/lib/useTimeTrackingFeatureAvailability'
import { useAutomationContextlessPolicies } from 'modules/automations/hooks/useAutomationContextlessPolicies'
import subscriptions from 'modules/billing/store/collections/subscriptions'
import { hasSurveysModule } from 'modules/engagement/lib/hasSurveysModule'
import { useTimeSettingsContextlessPolicies } from 'modules/time_categories_settings/hooks/useTimeSettingsContextlessPolicies'
import { timeoffModuleEnabledInAvailabilities } from 'modules/timeoff/lib/timeoffModuleEnabled'

import { SettingId, getSettingsItemsData } from './data'

const hasSpecificImportPermissions = (policies: SettingsPageQuery | undefined): boolean => {
  if (!policies) return false

  const importPermissionMappings = [{ module: 'itManagement', policy: 'manageIt' }]

  return importPermissionMappings.some(({ module }) => {
    if (!policies) return false

    // Check specific modules we know exist
    if (module === 'itManagement') {
      return policies.itManagement?._policies?.manageIt?.can === true
    }

    // Add more modules here as needed
    return false
  })
}

export interface Settings {
  id: string
  icon: React.ComponentProps<typeof Icon.Large>['icon']
  title: string
  children: SettingsChildren[]
}

export interface SettingsChildren {
  id: string
  icon: React.ComponentProps<typeof Icon.Large>['icon']
  title: string
  description: string
  url: string
  filter?: () => boolean | undefined
  keywords?: readonly string[]
  additionalInfo?: string
  missingInfo?: boolean
  hidden?: boolean
}

type SettingVisibilityInfo = {
  id: SettingId
  filter: () => boolean | undefined
  missingInfo?: boolean
  hidden?: boolean
}

const useSettingsVisibilityInfo = ({
  moduleAvailabilities = [],
  policies,
  seeAllSettingsPages,
  manageCustomNotifications,
  accessEditSettings,
  manageTimeRangeCategories,
  manageBreakConfigurations,
}: {
  moduleAvailabilities?: ModuleAvailabilityType[]
  policies?: SettingsPageQuery
  seeAllSettingsPages?: boolean
  manageCustomNotifications?: boolean
  accessEditSettings?: boolean
  manageTimeRangeCategories?: boolean
  manageBreakConfigurations?: boolean
}): SettingVisibilityInfo[] => {
  const { company } = useCurrentSession()

  if (!policies) {
    return []
  }

  return [
    {
      id: 'company_details',
      filter: () => seeAllSettingsPages,
      missingInfo: !company?.industry,
    },
    {
      id: 'workplaces',
      filter: () => !!policies.locations._policies.locationManage.can,
    },
    {
      id: 'subscription',
      filter: () =>
        subscriptions.current.inPaidPlan &&
        !!policies.billing._policies.subscriptionManageSubscription.can,
    },
    {
      id: 'customizations',
      filter: () => !!policies.apiCore._policies.companyManageCustomization.can,
    },
    {
      id: 'permissions',
      filter: () => !!policies.apiCore._policies.companyManagePermissions.can,
    },
    {
      id: 'documents',
      filter: () =>
        !!policies.documents._policies.companyManageFolders.can ||
        !!policies.documents._policies.companyManagePublicCompanyFolders.can ||
        !!policies.documents._policies.companyManageEmployeesFolders.can,
    },
    {
      id: 'external_users',
      filter: () =>
        !!policies.permissions._policies.editExternalUsers.can ||
        !!policies.permissions._policies.groupCreateExternalUsers.can,
    },
    {
      id: 'company-page',
      filter: () => !!policies.companyPage._policies.manageCompanyPage.can,
    },
    {
      id: 'security',
      filter: () => !!policies.securitySettings._policies.updateCompanyConfig.can,
    },
    {
      id: 'contracts',
      filter: () => !!policies.contracts._policies.contractEditContractsSettings.can,
    },
    {
      id: 'referral_program',
      filter: () => !!policies.apiCore._policies.seeReferralProgramPage.can,
    },
    {
      id: 'time_off',
      filter: () =>
        timeoffModuleEnabledInAvailabilities(moduleAvailabilities) &&
        !!policies.timeoff._policies.companyManageTimeoff.can,
    },
    {
      id: 'time_tracking',
      filter: () =>
        timeTrackingModuleEnabledInAvailabilities(moduleAvailabilities) && accessEditSettings,
    },
    {
      id: 'work_schedules',
      filter: () => !!policies.workSchedule._policies.manageCompanySchedules.can,
    },
    {
      id: 'time_categories',
      filter: () => manageTimeRangeCategories || manageBreakConfigurations,
    },

    {
      id: 'complaints',
      filter: () =>
        isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.complaints) &&
        seeAllSettingsPages,
    },
    {
      id: 'onboarding',
      filter: () =>
        !!policies.features.featuresConnection.nodes.find(
          (feature) => feature.name === 'employee_onboarding'
        )?.enabled && seeAllSettingsPages,
    },
    {
      id: 'engagement',
      filter: () => policies.engagement._policies.settingsManage.can,
    },
    {
      id: 'recruitment',
      filter: () => checkAtsEnabled(moduleAvailabilities) && !!policies.ats._policies.useAts.can,
    },
    {
      id: 'performance_process',
      filter: () => policies.performance._policies.settingsSeePerformanceReviewProcessSettings.can,
    },
    {
      id: 'surveys',
      filter: () =>
        hasSurveysModule(moduleAvailabilities) &&
        !!policies.surveys._policies.createSurveyTemplates.can,
    },
    {
      id: 'trainings',
      filter: () => policies.trainings._policies.sidebarSeeTrainingsTopLevelItem.can,
    },
    {
      id: 'ticketing',
      filter: () => !!policies.ticketing?._policies?.settingsSeeAiTicketingSettings?.can,
    },
    {
      id: 'it_inventory',
      filter: () =>
        isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.it_management_v2) &&
        !!policies.itManagement?._policies?.manageIt?.can,
    },
    {
      id: 'payroll',
      filter: () => policies.payroll._policies.settingsSeePayrollSettings.can,
    },
    {
      id: 'compensations',
      filter: () => policies.compensations._policies.settingsSeeCompensationsSettings.can,
    },
    {
      id: 'benefits',
      filter: () => policies.benefits._policies.sidebarSeeBenefitsTopLevelItem.can,
    },
    {
      id: 'salary_advance',
      filter: () =>
        isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.salary_advance) &&
        (!!policies.salaryAdvance?._policies?.manageSalaryAdvance?.can ||
          !!policies.finance?._policies?.sidebarSeeFinancePaymentsSubLevelItem?.can),
    },
    {
      id: 'software',
      filter: () => policies.softwareManagement._policies.settingsSeeSoftwareSettings.can,
    },
    {
      id: 'expenses',
      filter: () => policies.expenses._policies.settingsSeeExpensesSettings.can,
    },
    {
      id: 'cost_centers',
      filter: () => policies.finance._policies.settingsSeeCostCentersSettings.can,
    },
    {
      id: 'financial_workspace',
      filter: () => policies.financialWorkspace._policies.seeFinancialWorkspace.can,
    },
    {
      id: 'accounting',
      filter: () => policies.finance._policies.settingsSeeAccountingSettings.can,
    },
    {
      id: 'sales',
      filter: () => policies.finance._policies.settingsSeeSalesSettings.can,
    },
    {
      id: 'purchase_requests',
      filter: () => policies.procurement._policies.settingsSeeProcurementSettings.can,
    },
    {
      id: 'api_keys',
      filter: () => !!policies.apiPublic._policies.coreApiKeyManage.can,
    },
    {
      id: 'audit_log',
      filter: () =>
        !!policies.features.featuresConnection.nodes.find((feature) => feature.name === 'audit_log')
          ?.enabled && seeAllSettingsPages,
    },
    {
      id: 'custom_notifications',
      filter: () => manageCustomNotifications,
    },
    {
      id: 'approvals',
      filter: () => !!policies.approvals._policies.approvalGroupsEdit.can,
      hidden: true,
    },
    {
      id: 'universal_importer',
      filter: () =>
        !!policies.universalImporter._policies.importResources.can ||
        // Allow access if user has specific import permissions for any module
        hasSpecificImportPermissions(policies),
    },
  ] as const
}

export const useFilteredSettingsData = (
  navigation: Navigation<NavigationPaths>,
  moduleAvailabilities: ModuleAvailabilityType[],
  policies: SettingsPageQuery | undefined,
  seeAllSettingsPages: boolean
) => {
  const items = getSettingsItemsData({
    navigation,
  })

  const { manageCustomNotifications } = useAutomationContextlessPolicies()
  const { accessEditSettings } = useAttendanceContextlessPolicies()
  const { manageTimeRangeCategories, manageBreakConfigurations } =
    useTimeSettingsContextlessPolicies()

  const visibilityInfo = useSettingsVisibilityInfo({
    moduleAvailabilities,
    policies,
    seeAllSettingsPages,
    manageCustomNotifications,
    accessEditSettings,
    manageTimeRangeCategories,
    manageBreakConfigurations,
  })

  return items
    .map((settingCategory) => {
      const children = settingCategory.children
        .map((setting) => {
          const visibility = visibilityInfo.find((inf) => inf.id === setting.id)
          return {
            ...setting,
            ...(visibility ?? {}),
          }
        })
        .filter((setting) => setting.filter?.() ?? true)

      return { ...settingCategory, children }
    })
    .filter((settingCategory) => settingCategory.children.length > 0)
}

export const canAccessSettings = (settingsGroups: Settings[]) => {
  return settingsGroups.some((group) =>
    group.children.some((setting) => setting.filter?.() ?? true)
  )
}

export const settingsChildrenFlatMap = (settingsGroups: Settings[]) => {
  return settingsGroups.flatMap((settings) => {
    return settings.children.map((setting) => {
      return { category: settings.id, ...setting }
    })
  })
}
